import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  FINANCIAL_INVESTMENTS_DATE_COLUMN_INDEXES,
  FINANCIAL_INVESTMENTS_TABLE_ARIA_LABEL,
  FINANCIAL_INVESTMENTS_TABLE_COLUMN_WIDTHS,
  FINANCIAL_INVESTMENTS_TABLE_HEADERS,
  FINANCIAL_INVESTMENTS_TABLE_MIN_WIDTH,
  FINANCIAL_INVESTMENTS_SECTION_TITLES,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/financialInvestments/constants.ts';
import { FinancialInvestmentRow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/financialInvestments/FinancialInvestmentRow.tsx';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import type { IFinancialInvestmentsSectionProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/financialInvestments/types.ts';
import {
  BorrowerType,
  FinancialStatementForm,
  type TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { createFinancialInvestment } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

export const FinancialInvestmentsSection: React.FC<IFinancialInvestmentsSectionProps> = ({
  disabled,
}) => {
  const { control, watch } = useFormContext<TFinancialConditionFormValues>();
  const borrowerType = watch('borrowerType')?.value;
  const financialStatementForm = watch('financialStatementForm')?.value as
    FinancialStatementForm | undefined;
  const isLegalEntity = borrowerType === BorrowerType.LegalEntity;
  const { fields, append, remove } = useFieldArray({ control, name: 'financialInvestments' });
  const canRemoveInvestment = fields.length > 1;

  if (!isLegalEntity || !financialStatementForm) {
    return null;
  }

  return (
    <Box sx={documentGenerationFormStyles.section}>
      <Typography component="h3" sx={documentGenerationFormStyles.sectionTitle}>
        {FINANCIAL_INVESTMENTS_SECTION_TITLES[financialStatementForm]}
      </Typography>
      <FinancialConditionTable
        ariaLabel={FINANCIAL_INVESTMENTS_TABLE_ARIA_LABEL}
        headers={[...FINANCIAL_INVESTMENTS_TABLE_HEADERS]}
        showActions={canRemoveInvestment}
        dateColumnIndexes={[...FINANCIAL_INVESTMENTS_DATE_COLUMN_INDEXES]}
        columnWidths={[...FINANCIAL_INVESTMENTS_TABLE_COLUMN_WIDTHS]}
        minWidth={FINANCIAL_INVESTMENTS_TABLE_MIN_WIDTH}
      >
        {fields.map((field, index) => (
          <FinancialInvestmentRow
            key={field.id}
            index={index}
            canRemove={canRemoveInvestment}
            disabled={disabled}
            onRemove={() => remove(index)}
          />
        ))}
      </FinancialConditionTable>
      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => append(createFinancialInvestment())}
        disabled={disabled}
        sx={documentGenerationFormStyles.addButton}
      >
        Додати фінансову інвестицію
      </Button>
    </Box>
  );
};
